import {ReactElement, useEffect, useRef, useState} from 'react';
import merge from 'lodash/merge';
import {useElementSize, useViewportSize} from "@mantine/hooks";
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {useService} from '@yosmanyga/di';
import {Button, Container, Icon, useIcons} from '@yosmanyga/rich-ui';
import {ThemeBuildType, ThemeProvider, ThemeVariablesType} from '@yosmanyga/theme';
import {AppUnreadFeedbackController, Menu, Play, ProfileController} from '@yosmanyga/fortaleza-core';
import {ManuscriptLayout} from '../ManuscriptLayout';

const PlayScreen = observer(() => {
    const navigate = useNavigate();

    const storyRef = useRef<HTMLDivElement>(null);

    const PlayLayout = observer(({
        story,
        input
    }: {
        story: ReactElement,
        input: ReactElement
    }) => {
        const [menu, setMenu] = useState(false);

        const icons = useIcons();
        const navigate = useNavigate();

        const {ref: menuRef, height: menuHeight} = useElementSize();
        const {ref: inputRef, height: inputHeight} = useElementSize();
        const {height} = useViewportSize();

        const profileController = useService(ProfileController, []);
        const unreadFeedbackController = useService(AppUnreadFeedbackController, []);

        useEffect(() => {
            (async () => {
                await profileController.load();
                await unreadFeedbackController.load();
            })();
        }, []);

        // This can't be used, because then the heights are not calculated at the first render
        // if (
        //     !profileController.loaded
        //     || !unreadFeedbackController.loaded
        // ) {
        //     return null;
        // }

        return <>
            <ThemeProvider
                build={(variables?: ThemeVariablesType, prev?: ThemeBuildType) => {
                    const {palette} = variables as ThemeVariablesType;

                    return merge(
                        {},
                        prev && prev(variables), {
                            button: {
                                color: palette.neutral['800'],
                                pseudo: {
                                    hover: {
                                        color: palette.neutral['900']
                                    },
                                },
                            },
                        }
                    );
                }}
                variables={undefined}
            >
                <Container
                    ref={menuRef}
                    padding={1}
                    position={{
                        type: 'absolute',
                        top: 0,
                        right: 0,
                    }}
                    z={999}
                >
                    <Button
                        onClick={() => {
                            setMenu(true);
                        }}
                        margin={{
                            right: 2,
                        }}
                    >
                        <Icon
                            component={icons.objects.menu}
                            size='xs'
                        />
                    </Button>
                </Container>
            </ThemeProvider>

            <ManuscriptLayout>
                <Container
                    ref={storyRef}
                    scroll
                    height={height - menuHeight * 2.5 - inputHeight}
                >
                    {story}
                </Container>

                <Container
                    ref={inputRef}
                    margin={{
                        top: 1,
                    }}
                >
                    {input}
                </Container>
            </ManuscriptLayout>

            {menu
                && profileController.loaded
                && unreadFeedbackController.loaded
                && <Menu
                    unread={unreadFeedbackController.unread}
                    version={profileController.version}
                    onClose={() => {
                        setMenu(false);
                    }}
                    onNavigateToNotes={() => {
                        navigate('notes');
                    }}
                    onNavigateToHelp={() => {
                        navigate('help');
                    }}
                    onNavigateToMap={() => {
                        navigate('map');
                    }}
                    onNavigateToFeedback={() => {
                        navigate('feedback');
                    }}
                    onNavigateToRestoration={() => {
                        navigate('restoration');
                    }}
                    onNavigateToRestart={() => {
                        navigate('restart');
                    }}
                    onNavigateToBuy={() => {
                        navigate('payment');
                    }}
                />}
        </>
    });

    return <Play
        templates={{
            layout: PlayLayout,
        }}
        onInput={() => {
            if (!storyRef.current) {
                return;
            }

            storyRef.current.scrollTop = storyRef.current.scrollHeight;
        }}
        onNavigateToRestore={() => {
            navigate('restoration');
        }}
        onNavigateToHelp={() => {
            navigate('help');
        }}
    />
});

export {PlayScreen};
